import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus, faSync, faEyeSlash, faEye, faComments, faTimes, faCheckCircle, faTimesCircle, faChevronDown, faChevronRight, faExclamationTriangle, faEdit, faBan, faSearch } from '@fortawesome/free-solid-svg-icons';
import { Card, Form, Button, Alert, Table, Badge, Col, Row, Modal, InputGroup, ProgressBar, Pagination } from '@themesberg/react-bootstrap';
import { collection, query, where, onSnapshot, doc, deleteDoc, updateDoc, orderBy, addDoc, serverTimestamp, getDocs, limit } from 'firebase/firestore';
import { uploadToR2, generateUUID } from '../../utils/r2Upload';
import {  db } from '../../config/firebase';

// Video'dan küçük resim (thumbnail) oluştur
const generateVideoThumbnail = (file) => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.src = URL.createObjectURL(file);
    video.onloadedmetadata = () => {
      video.currentTime = 1; // 1. saniyedeki kareyi al
    };
    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (blob) {
          const thumbFile = new File([blob], 'thumb.jpg', { type: 'image/jpeg' });
          resolve(thumbFile);
        } else {
          resolve(null);
        }
        URL.revokeObjectURL(video.src);
      }, 'image/jpeg', 0.8);
    };
    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      resolve(null);
    };
  });
};

export default () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [filterType, setFilterType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [fakeAccounts, setFakeAccounts] = useState([]);
  const [filteredFakeAccounts, setFilteredFakeAccounts] = useState([]);
  const [currentAuthorPage, setCurrentAuthorPage] = useState(1);
  const [authorSearchText, setAuthorSearchText] = useState("");
  const authorsPerPage = 12;

  // Post creation state
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountSearchTerm, setAccountSearchTerm] = useState("");
  const [postText, setPostText] = useState("");
  const [posting, setPosting] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [postType, setPostType] = useState("post");
  const [isPublic, setIsPublic] = useState(false);
  const [autoDeleteUnit, setAutoDeleteUnit] = useState("day");
  const [autoDeleteValue, setAutoDeleteValue] = useState("");

  // Comment modal
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [selectedPostForComments, setSelectedPostForComments] = useState(null);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState("");

  // Kategorize görünüm
  const [expandedUsers, setExpandedUsers] = useState({});
  const EXPAND_STEP = 15;

  // Toplu silme
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showUserDeleteModal, setShowUserDeleteModal] = useState(false);
  const [deleteTargetUser, setDeleteTargetUser] = useState(null);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState(null);
  const [thumbProgress, setThumbProgress] = useState(null); // {current, total, status}

  // Posts yükle
  useEffect(() => {
    const q = query(
      collection(db, 'content'),
      where('type', '==', 'post'),
      orderBy('createdAt', 'desc'),
      limit(1500)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const postsList = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        postsList.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || null
        });
      });
      setPosts(postsList);
      setFilteredPosts(postsList);
      setLoading(false);
    }, (err) => {
      console.error("Error loading posts:", err);
      setError("Paylaşımlar yüklenirken hata oluştu: " + err.message);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Fake hesapları yükle
  useEffect(() => {
    const q = query(collection(db, 'users'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const accounts = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.isTestUser === true || data.isFake === true) {
          accounts.push({
            id: doc.id,
            name: data.name || data.firstName || 'Bilinmeyen',
            profileImageUrl: data.profileImageUrl,
            isGoldProfile: data.isGoldProfile || false,
            isApproved: data.isApproved || false,
            isTestUser: data.isTestUser || false
          });
        }
      });
      setFakeAccounts(accounts);
      setFilteredFakeAccounts(accounts);
    }, (err) => {
      console.error("Error loading fake accounts:", err);
    });

    return () => unsubscribe();
  }, []);

  // Hesap arama filtresi
  useEffect(() => {
    if (!accountSearchTerm) {
      setFilteredFakeAccounts(fakeAccounts);
      return;
    }
    const searchLower = accountSearchTerm.toLowerCase();
    const filtered = fakeAccounts.filter(account =>
      account.name.toLowerCase().includes(searchLower)
    ).sort((a, b) => a.name.localeCompare(b.name));
    setFilteredFakeAccounts(filtered);
  }, [accountSearchTerm, fakeAccounts]);

  // Filtreleme
  useEffect(() => {
    if (!filterType) {
      setFilteredPosts(posts);
      return;
    }

    const now = new Date();
    let filtered = [...posts];

    if (filterType === "testAccounts") {
      const testAccountIds = new Set(fakeAccounts.map(acc => acc.id));
      filtered = filtered.filter(post => testAccountIds.has(post.authorId || post.userId));
    } else if (filterType === "last24Hours") {
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      filtered = filtered.filter(post => post.createdAt && post.createdAt >= yesterday);
    } else if (filterType === "last7Days") {
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(post => post.createdAt && post.createdAt >= sevenDaysAgo);
    }

    setFilteredPosts(filtered);
  }, [filterType, posts, fakeAccounts]);

  // Filtre değiştiğinde sayfayı sıfırla
  useEffect(() => {
    setCurrentAuthorPage(1);
  }, [filterType, accountSearchTerm, authorSearchText]);

  // Postları kullanıcıya göre grupla
  const groupedPosts = React.useMemo(() => {
    const groups = {};
    filteredPosts.forEach(post => {
      const key = post.authorId || post.userId || 'unknown';
      if (!groups[key]) {
        groups[key] = {
          userId: key,
          userName: post.authorName || 'Bilinmeyen',
          userAvatar: post.authorAvatar,
          posts: []
        };
      }
      groups[key].posts.push(post);
    });
    return Object.values(groups).sort((a, b) => b.posts.length - a.posts.length);
  }, [filteredPosts]);

  // Arama filtresi uygulanmış yazarlar
  const searchedAuthors = React.useMemo(() => {
    if (!authorSearchText) return groupedPosts;
    return groupedPosts.filter(g => g.userName.toLowerCase().includes(authorSearchText.toLowerCase()));
  }, [groupedPosts, authorSearchText]);

  // Sayfalanmış yazarlar
  const paginatedAuthors = React.useMemo(() => {
    const startIndex = (currentAuthorPage - 1) * authorsPerPage;
    return searchedAuthors.slice(startIndex, startIndex + authorsPerPage);
  }, [searchedAuthors, currentAuthorPage]);

  const totalAuthorPages = Math.ceil(searchedAuthors.length / authorsPerPage);

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString('tr-TR', {
      timeZone: 'Europe/Istanbul',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // Medya seçimi
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const newMedia = files.map(file => ({
        file,
        preview: URL.createObjectURL(file),
        text: ""
      }));
      setSelectedMedia(prev => [...prev, ...newMedia]);
    }
  };

  const removeMedia = (index) => {
    setSelectedMedia(prev => prev.filter((_, i) => i !== index));
  };

  const updateMediaText = (index, newText) => {
    setSelectedMedia(prev => prev.map((item, i) => i === index ? { ...item, text: newText } : item));
  };

  // Paylaşım gönder
  const handlePost = async () => {
    if (!selectedAccount) return;
    if (postType === "story" && selectedMedia.length === 0) {
      setError("Hikaye için en az bir fotoğraf zorunludur.");
      return;
    }
    if (postType === "post" && !postText.trim() && selectedMedia.length === 0) {
      setError("Gönderi için en az metin veya fotoğraf gereklidir.");
      return;
    }

    setPosting(true);
    setError(null);

    try {
      const accountDoc = await getDocs(query(collection(db, 'users'), where('__name__', '==', selectedAccount.id)));
      const accountData = accountDoc.docs[0]?.data() || {};

      let deleteAt = null;
      if (autoDeleteValue && !isNaN(autoDeleteValue) && parseFloat(autoDeleteValue) > 0) {
        const ms = autoDeleteUnit === "hour"
          ? parseFloat(autoDeleteValue) * 60 * 60 * 1000
          : parseFloat(autoDeleteValue) * 24 * 60 * 60 * 1000;
        deleteAt = new Date(Date.now() + ms);
      }

      if (selectedMedia.length > 0) {
        let successCount = 0;
        for (const media of selectedMedia) {
          const r2Path = `social/posts/${selectedAccount.id}/${generateUUID()}_${media.file.name}`;
          const downloadUrl = await uploadToR2(media.file, r2Path);
          
          const isVideo = media.file.type.startsWith('video/');

          // Video ise küçük resim oluştur ve yükle
          let thumbnailUrl = null;
          if (isVideo) {
            try {
              const thumbFile = await generateVideoThumbnail(media.file);
              if (thumbFile) {
                const thumbPath = `social/posts/${selectedAccount.id}/${generateUUID()}_thumb.jpg`;
                thumbnailUrl = await uploadToR2(thumbFile, thumbPath);
              }
            } catch (thumbErr) {
              console.warn('Thumbnail oluşturulamadı:', thumbErr);
            }
          }

          if (postType === "post") {
            await addDoc(collection(db, 'content'), {
              type: "post",
              userId: selectedAccount.id,
              authorId: selectedAccount.id,
              authorName: accountData.name || selectedAccount.name,
              authorAvatar: accountData.profileImageUrl || selectedAccount.profileImageUrl,
              authorIsPremium: accountData.isPremium === true,
              authorIsApproved: accountData.isApproved === true,
              authorIsTestUser: accountData.isTestUser === true,
              text: media.text.trim() || null,
              imageUrl: isVideo ? thumbnailUrl : downloadUrl,
              videoUrl: isVideo ? downloadUrl : null,
              thumbnailUrl: isVideo ? thumbnailUrl : null,
              isVideo: isVideo,
              createdAt: serverTimestamp(),
              likeCount: 0,
              commentCount: 0,
              likedBy: [],
              isVisibleToOthers: (accountData.isGoldProfile && !isPublic) ? false : (accountData.isTestUser === true || accountData.isApproved === true),
              isPublic: isPublic,
              deleteAt: deleteAt ? deleteAt.toISOString() : null
            });
          } else {
            await addDoc(collection(db, 'content'), {
              type: "story",
              userId: selectedAccount.id,
              authorId: selectedAccount.id,
              authorName: accountData.name || selectedAccount.name,
              authorAvatar: accountData.profileImageUrl || selectedAccount.profileImageUrl,
              authorIsApproved: accountData.isApproved === true || accountData.isTestUser === true,
              imageUrl: isVideo ? thumbnailUrl : downloadUrl,
              videoUrl: isVideo ? downloadUrl : null,
              thumbnailUrl: isVideo ? thumbnailUrl : null,
              isVideo: isVideo,
              createdAt: serverTimestamp(),
              likeCount: 0,
              isVisibleToOthers: (accountData.isGoldProfile && !isPublic) ? false : (accountData.isTestUser === true || accountData.isApproved === true),
              isPublic: isPublic,
              deleteAt: deleteAt ? deleteAt.toISOString() : null
            });
          }
          successCount++;
        }
        setSuccess(`${successCount} paylaşım başarıyla oluşturuldu.`);
      } else {
        if (postType === "post") {
          await addDoc(collection(db, 'content'), {
            type: "post",
            userId: selectedAccount.id,
            authorId: selectedAccount.id,
            authorName: accountData.name || selectedAccount.name,
            authorAvatar: accountData.profileImageUrl || selectedAccount.profileImageUrl,
            authorIsPremium: accountData.isPremium === true,
            authorIsApproved: accountData.isApproved === true,
            authorIsTestUser: accountData.isTestUser === true,
            text: postText.trim() || null,
            imageUrl: null,
            createdAt: serverTimestamp(),
            likeCount: 0,
            commentCount: 0,
            likedBy: [],
            isVisibleToOthers: (accountData.isGoldProfile && !isPublic) ? false : (accountData.isTestUser === true || accountData.isApproved === true),
            isPublic: isPublic,
            deleteAt: deleteAt ? deleteAt.toISOString() : null
          });
          setSuccess("Paylaşım başarıyla oluşturuldu.");
        }
      }

      setShowPostModal(false);
      setSelectedAccount(null);
      setPostText("");
      setSelectedMedia([]);
      setPostType("post");
      setAutoDeleteUnit("day");
      setAutoDeleteValue("");
      setIsPublic(false);

      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Error creating post:", err);
      setError("Paylaşım oluşturulurken hata: " + err.message);
    } finally {
      setPosting(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Bu paylaşımı kalıcı olarak silmek istediğinizden emin misiniz?")) return;
    setActionLoading(postId);
    try {
      await deleteDoc(doc(db, 'content', postId));
      setSuccess("Paylaşım başarıyla silindi.");
    } catch (err) {
      setError("Silme hatası: " + err.message);
    } finally {
      setActionLoading(null);
    }
  };



  const handleEditComment = (comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentText(comment.text);
  };

  const handleSaveComment = async (commentId) => {
    if (!selectedPostForComments) return;
    try {
      await updateDoc(doc(db, 'content', selectedPostForComments.id, 'comments', commentId), {
        text: editingCommentText
      });
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, text: editingCommentText } : c));
      setEditingCommentId(null);
    } catch (err) {
      console.error("Error editing comment:", err);
      setError("Yorum güncellenirken hata oluştu.");
    }
  };

  const handleCensorComment = async (commentId) => {
    if (!selectedPostForComments) return;
    const censoredText = "**** (Bu yorum kurallara aykırı olduğu için sansürlenmiştir)";
    try {
      await updateDoc(doc(db, 'content', selectedPostForComments.id, 'comments', commentId), {
        text: censoredText
      });
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, text: censoredText } : c));
      setSuccess("Yorum başarıyla sansürlendi.");
    } catch (err) {
      console.error("Error censoring comment:", err);
      setError("Yorum sansürlenirken hata oluştu.");
    }
  };

  const handleToggleVisibility = async (post) => {
    const newVisibility = !(post.isVisibleToOthers !== false);
    setActionLoading(post.id);
    try {
      await updateDoc(doc(db, 'content', post.id), {
        isVisibleToOthers: newVisibility
      });
      setSuccess(`Paylaşım artık ${newVisibility ? 'herkese açık' : 'gizli'}.`);
    } catch (err) {
      setError("Güncelleme hatası: " + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRepublishPost = async (post) => {
    if (!window.confirm("Bu paylaşımı güncel tarihle yeniden yayınlamak istiyor musunuz?")) return;
    setActionLoading(post.id);
    try {
      const { id, ...postData } = post;
      await addDoc(collection(db, 'content'), {
        ...postData,
        type: 'post',
        createdAt: serverTimestamp(),
        likeCount: 0,
        commentCount: 0,
        likedBy: [],
        isVisibleToOthers: true
      });
      setSuccess("Paylaşım başarıyla yeniden yayınlandı.");
    } catch (err) {
      setError("Yeniden yayınlama hatası: " + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleViewComments = async (post) => {
    setSelectedPostForComments(post);
    setShowCommentsModal(true);
    setLoadingComments(true);
    try {
      const commentsSnapshot = await getDocs(
        query(
          collection(db, 'content', post.id, 'comments'),
          orderBy('createdAt', 'desc')
        )
      );
      const commentsList = [];
      commentsSnapshot.forEach(doc => {
        const data = doc.data();
        commentsList.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || null
        });
      });
      setComments(commentsList);
    } catch (err) {
      console.error("Error loading comments:", err);
      setError("Yorumlar yüklenirken hata: " + err.message);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!selectedPostForComments) return;
    if (!window.confirm("Bu yorumu silmek istediğinizden emin misiniz?")) return;
    setDeletingCommentId(commentId);
    try {
      await deleteDoc(doc(db, 'content', selectedPostForComments.id, 'comments', commentId));
      setComments(prev => prev.filter(c => c.id !== commentId));
      // Yorum sayısını güncelle
      const newCount = Math.max(0, (selectedPostForComments.commentCount || 1) - 1);
      await updateDoc(doc(db, 'content', selectedPostForComments.id), {
        commentCount: newCount
      });
      setSuccess("Yorum silindi.");
    } catch (err) {
      setError("Yorum silme hatası: " + err.message);
    } finally {
      setDeletingCommentId(null);
    }
  };

  const handleDeleteAllComments = async () => {
    if (!selectedPostForComments || comments.length === 0) return;
    if (!window.confirm("Bu paylaşımdaki tüm yorumları kalıcı olarak silmek istediğinizden emin misiniz?")) return;
    
    setActionLoading("deleteAllComments");
    try {
      let deleted = 0;
      for (const comment of comments) {
        await deleteDoc(doc(db, 'content', selectedPostForComments.id, 'comments', comment.id));
        deleted++;
      }
      setComments([]);
      await updateDoc(doc(db, 'content', selectedPostForComments.id), {
        commentCount: 0
      });
      setSuccess(`${deleted} yorum başarıyla silindi.`);
    } catch (err) {
      setError("Toplu yorum silme hatası: " + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  // Kullanıcının tüm paylaşımlarını sil
  const handleDeleteUserPosts = async () => {
    if (!deleteTargetUser) return;
    const { userId, count } = deleteTargetUser;
    setBulkDeleteProgress({ current: 0, total: count });
    setShowUserDeleteModal(false);
    try {
      const userPosts = filteredPosts.filter(p => (p.authorId || p.userId) === userId);
      let deleted = 0;
      for (const post of userPosts) {
        await deleteDoc(doc(db, 'content', post.id));
        deleted++;
        setBulkDeleteProgress({ current: deleted, total: count });
      }
      setSuccess(`${deleted} paylaşım başarıyla silindi.`);
    } catch (err) {
      setError("Toplu silme hatası: " + err.message);
    } finally {
      setBulkDeleteProgress(null);
      setDeleteTargetUser(null);
    }
  };

  const handleBulkDeleteAll = async () => {
    setShowBulkDeleteModal(false);
    const total = filteredPosts.length;
    setBulkDeleteProgress({ current: 0, total });
    try {
      let deleted = 0;
      for (const post of filteredPosts) {
        await deleteDoc(doc(db, 'content', post.id));
        deleted++;
        setBulkDeleteProgress({ current: deleted, total });
      }
      setSuccess(`${deleted} paylaşım tamamen silindi.`);
    } catch (err) {
      setError("Toplu silme hatası: " + err.message);
    } finally {
      setBulkDeleteProgress(null);
    }
  };

  const handleGlobalDeleteComments = async () => {
    if (!window.confirm("Sistemdeki tüm (yüklenen) gönderilerin yorumları kalıcı olarak silinecek. Emin misiniz?")) return;
    setActionLoading("globalDeleteComments");
    let deletedCommentsTotal = 0;
    try {
      for (const post of filteredPosts) {
        const commentsSnap = await getDocs(collection(db, 'content', post.id, 'comments'));
        if (!commentsSnap.empty) {
          for (const cDoc of commentsSnap.docs) {
            await deleteDoc(doc(db, 'content', post.id, 'comments', cDoc.id));
            deletedCommentsTotal++;
          }
          await updateDoc(doc(db, 'content', post.id), { commentCount: 0 });
        } else if (post.commentCount > 0) {
          await updateDoc(doc(db, 'content', post.id), { commentCount: 0 });
        }
      }
      setSuccess(`İşlem tamamlandı: Toplam ${deletedCommentsTotal} yorum silindi.`);
    } catch (err) {
      setError("Toplu yorum silme hatası: " + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const toggleExpand = (userId) => {
    setExpandedUsers(prev => {
      const current = prev[userId] || 0;
      if (current === 0) {
        return { ...prev, [userId]: EXPAND_STEP };
      } else {
        return { ...prev, [userId]: 0 };
      }
    });
  };

  const showMore = (userId, totalCount) => {
    setExpandedUsers(prev => {
      const current = prev[userId] || 0;
      return { ...prev, [userId]: Math.min(current + EXPAND_STEP, totalCount) };
    });
  };

  // Eksik thumbnail'leri toplu oluştur
  const handleGenerateMissingThumbnails = async () => {
    const videosWithoutThumb = posts.filter(p => {
      const hasVideo = p.videoUrl != null;
      const hasThumb = p.imageUrl != null || p.thumbnailUrl != null;
      return hasVideo && !hasThumb;
    });

    if (videosWithoutThumb.length === 0) {
      setSuccess('Tüm videoların thumbnail\'i zaten mevcut!');
      return;
    }

    if (!window.confirm(`${videosWithoutThumb.length} video için thumbnail oluşturulacak. Devam?`)) return;

    setThumbProgress({ current: 0, total: videosWithoutThumb.length, status: 'Başlatılıyor...' });
    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < videosWithoutThumb.length; i++) {
      const post = videosWithoutThumb[i];
      setThumbProgress({ current: i + 1, total: videosWithoutThumb.length, status: `${post.authorName || 'Bilinmeyen'} - ${post.id.slice(0, 8)}` });

      try {
        const thumbBlob = await new Promise((resolve) => {
          const video = document.createElement('video');
          video.crossOrigin = 'anonymous';
          video.preload = 'metadata';
          video.src = post.videoUrl;
          video.onloadedmetadata = () => { video.currentTime = 1; };
          video.onseeked = () => {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => {
              URL.revokeObjectURL(video.src);
              resolve(blob);
            }, 'image/jpeg', 0.8);
          };
          video.onerror = () => { URL.revokeObjectURL(video.src); resolve(null); };
          setTimeout(() => resolve(null), 15000);
        });

        if (!thumbBlob) { failCount++; continue; }

        const thumbFile = new File([thumbBlob], 'thumb.jpg', { type: 'image/jpeg' });
        const thumbPath = `social/posts/${post.authorId || post.userId || 'unknown'}/${generateUUID()}_thumb.jpg`;
        const thumbUrl = await uploadToR2(thumbFile, thumbPath);

        await updateDoc(doc(db, 'content', post.id), {
          imageUrl: thumbUrl,
          thumbnailUrl: thumbUrl,
          isVideo: true
        });

        successCount++;
      } catch (err) {
        console.error(`Thumbnail hatası (${post.id}):`, err);
        failCount++;
      }
    }

    setThumbProgress(null);
    setSuccess(`Thumbnail oluşturma tamamlandı: ${successCount} başarılı, ${failCount} hatalı`);
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>Gönderiler</h4>
          <p className="mb-0 text-muted">
            Uygulamadaki gönderileri yönetin.
            {!loading && <span className="ms-2"><strong>{filteredPosts.length}</strong> gönderi, <strong>{groupedPosts.length}</strong> kullanıcı</span>}
          </p>
        </div>
        <div className="btn-toolbar mb-2 mb-md-0 gap-2">
          {filteredPosts.length > 0 && (
            <>
              <Button variant="info" size="sm" onClick={handleGenerateMissingThumbnails} disabled={thumbProgress !== null || actionLoading !== null}>
                🖼️ Eksik Thumbnail
              </Button>
              <Button variant="warning" size="sm" onClick={handleGlobalDeleteComments} disabled={actionLoading !== null}>
                <FontAwesomeIcon icon={faComments} className="me-2" />
                Tüm Yorumları Sil
              </Button>
              <Button variant="danger" size="sm" onClick={() => setShowBulkDeleteModal(true)} disabled={bulkDeleteProgress !== null}>
                <FontAwesomeIcon icon={faTrash} className="me-2" />
                Tümünü Sil ({filteredPosts.length})
              </Button>
            </>
          )}
          <Button variant="primary" size="sm" onClick={() => setShowAccountModal(true)}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Yeni Paylaşım
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess(false)} dismissible>{typeof success === 'string' ? success : 'İşlem başarılı!'}</Alert>}

      {bulkDeleteProgress && (
        <Alert variant="warning" className="mb-3">
          <div className="d-flex align-items-center mb-2">
            <FontAwesomeIcon icon={faTrash} className="me-2" spin />
            Siliniyor: {bulkDeleteProgress.current} / {bulkDeleteProgress.total}
          </div>
          <ProgressBar
            now={(bulkDeleteProgress.current / bulkDeleteProgress.total) * 100}
            label={`${Math.round((bulkDeleteProgress.current / bulkDeleteProgress.total) * 100)}%`}
            variant="danger"
          />
        </Alert>
      )}

      {thumbProgress && (
        <Alert variant="info" className="mb-3">
          <div className="d-flex align-items-center mb-2">
            🖼️ Thumbnail oluşturuluyor: {thumbProgress.current} / {thumbProgress.total} — {thumbProgress.status}
          </div>
          <ProgressBar
            now={(thumbProgress.current / thumbProgress.total) * 100}
            label={`${Math.round((thumbProgress.current / thumbProgress.total) * 100)}%`}
            variant="info"
          />
        </Alert>
      )}

      <Card border="light" className="shadow-sm">
        <Card.Body className="pt-0">
          <div className="table-settings mb-4">
            <Row className="align-items-center justify-content-between">
              <Col xs={8} md={4} className="mt-3">
                <Form.Select value={filterType || ""} onChange={(e) => setFilterType(e.target.value || null)}>
                  <option value="">Filtrele: Tümü</option>
                  <option value="testAccounts">Sadece Fake Hesaplar</option>
                  <option value="last24Hours">Son 24 Saat</option>
                  <option value="last7Days">Son 7 Gün</option>
                </Form.Select>
              </Col>
              
              <Col xs={12} md={4} className="mt-3">
                <InputGroup>
                  <InputGroup.Text>
                    <FontAwesomeIcon icon={faSearch} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="İsim ile veya kullanıcı adı ile ara..."
                    value={authorSearchText}
                    onChange={(e) => setAuthorSearchText(e.target.value)}
                  />
                </InputGroup>
              </Col>
            </Row>
          </div>

          {loading ? (
            <div className="text-center py-5">Yükleniyor...</div>
          ) : paginatedAuthors.length === 0 ? (
            <div className="text-center py-5 text-muted">Henüz paylaşım yok.</div>
          ) : (
            <>
              {paginatedAuthors.map((group) => {
                const visibleCount = expandedUsers[group.userId] || 0;
                const isOpen = visibleCount > 0;
                const visibleItems = group.posts.slice(0, visibleCount);
                const remaining = group.posts.length - visibleCount;

                return (
                  <div key={group.userId} className="mb-2">
                    {/* Kullanıcı Başlık Satırı */}
                    <div
                      className="d-flex align-items-center justify-content-between p-3"
                      style={{
                        background: isOpen ? '#e8edf5' : '#f8f9fa',
                        borderRadius: isOpen ? '8px 8px 0 0' : '8px',
                        cursor: 'pointer',
                        borderLeft: '4px solid',
                        borderLeftColor: isOpen ? '#4e73df' : '#adb5bd',
                        transition: 'all 0.2s'
                      }}
                      onClick={() => toggleExpand(group.userId)}
                    >
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon
                          icon={isOpen ? faChevronDown : faChevronRight}
                          className="me-3 text-muted"
                          style={{ width: '12px' }}
                        />
                        <img
                          src={group.userAvatar || "/avatar.png"}
                          className="avatar rounded-circle me-3"
                          style={{ width: 40, height: 40, objectFit: 'cover' }}
                          alt=""
                        />
                        <div>
                          <span className="fw-bold text-dark">{group.userName}</span>
                          <Badge bg="secondary" className="ms-2">{group.posts.length} gönderi</Badge>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2" onClick={e => e.stopPropagation()}>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            setDeleteTargetUser({
                              userId: group.userId,
                              userName: group.userName,
                              count: group.posts.length
                            });
                            setShowUserDeleteModal(true);
                          }}
                          disabled={bulkDeleteProgress !== null}
                          title={`${group.userName} - Tüm gönderileri sil`}
                        >
                          <FontAwesomeIcon icon={faTrash} className="me-1" />
                          Hepsini Sil
                        </Button>
                      </div>
                    </div>

                    {/* Gönderi Tablosu */}
                    {isOpen && (
                      <div style={{
                        background: '#ffffff',
                        borderRadius: '0 0 8px 8px',
                        border: '1px solid #dee2e6',
                        borderTop: 'none'
                      }}>
                        <Table hover className="user-table align-items-center mb-0" size="sm">
                          <thead>
                            <tr>
                              <th className="border-bottom" style={{ width: '70px', color: '#a0b4d0' }}>Görsel</th>
                              <th className="border-bottom" style={{ color: '#a0b4d0' }}>İçerik</th>
                              <th className="border-bottom" style={{ color: '#a0b4d0' }}>Beğeni</th>
                              <th className="border-bottom" style={{ color: '#a0b4d0' }}>Yorum</th>
                              <th className="border-bottom" style={{ color: '#a0b4d0' }}>Tarih</th>
                              <th className="border-bottom" style={{ color: '#a0b4d0' }}>Durum</th>
                              <th className="border-bottom" style={{ width: '160px', color: '#a0b4d0' }}>İşlem</th>
                            </tr>
                          </thead>
                          <tbody>
                            {visibleItems.map(post => (
                              <tr key={post.id}>
                                <td>
                                  {post.videoUrl ? (
                                    <video src={post.videoUrl} style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 6, cursor: 'pointer' }} onClick={() => window.open(post.videoUrl, '_blank')} />
                                  ) : post.imageUrl ? (
                                    <img src={post.imageUrl} alt="Post" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 6, cursor: 'pointer' }} onClick={() => window.open(post.imageUrl, '_blank')} />
                                  ) : (
                                    <span style={{ color: '#6c757d' }}>-</span>
                                  )}
                                </td>
                                <td style={{ color: '#e0e6ed' }}>
                                  <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {post.text ? (post.text.length > 80 ? `${post.text.substring(0, 80)}...` : post.text) : '-'}
                                  </div>
                                </td>
                                <td style={{ color: '#ccd6e0' }}>{post.likeCount || 0}</td>
                                <td style={{ color: '#ccd6e0' }}>
                                  <div className="d-flex align-items-center gap-1">
                                    <span>{post.commentCount || 0}</span>
                                    {(post.commentCount || 0) > 0 && (
                                      <Button variant="outline-info" size="sm" onClick={() => handleViewComments(post)} title="Yorumları Gör" style={{ padding: '1px 5px' }}>
                                        <FontAwesomeIcon icon={faComments} style={{ fontSize: '0.7rem' }} />
                                      </Button>
                                    )}
                                  </div>
                                </td>
                                <td style={{ color: '#ccd6e0' }}><small>{formatDate(post.createdAt)}</small></td>
                                <td>
                                  {post.isVisibleToOthers === false ? (
                                    <Badge bg="warning">Gizli</Badge>
                                  ) : (
                                    <Badge bg="success">Yayında</Badge>
                                  )}
                                </td>
                                <td>
                                  <div className="btn-group">
                                    <Button variant="outline-primary" size="sm" onClick={() => handleToggleVisibility(post)} disabled={actionLoading === post.id} title={post.isVisibleToOthers === false ? "Yayınla" : "Gizle"}>
                                      <FontAwesomeIcon icon={post.isVisibleToOthers === false ? faEye : faEyeSlash} />
                                    </Button>
                                    <Button variant="outline-info" size="sm" onClick={() => handleRepublishPost(post)} disabled={actionLoading === post.id} title="Yeniden Paylaş">
                                      <FontAwesomeIcon icon={faSync} />
                                    </Button>
                                    <Button variant="outline-danger" size="sm" onClick={() => handleDeletePost(post.id)} disabled={actionLoading === post.id} title="Sil">
                                      <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>

                        {/* +15 Daha Göster butonu */}
                        {remaining > 0 && (
                          <div className="text-center py-3">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => showMore(group.userId, group.posts.length)}
                            >
                              <FontAwesomeIcon icon={faChevronDown} className="me-2" />
                              +{Math.min(remaining, EXPAND_STEP)} daha göster
                              <span className="text-muted ms-2">({remaining} kalan)</span>
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Pagination UI */}
              {totalAuthorPages > 1 && (
                <div className="d-flex justify-content-between align-items-center p-3 mt-3 bg-light rounded shadow-sm">
                  <small className="text-muted">Toplam {groupedPosts.length} yazar üzerinden {currentAuthorPage}. sayfa gösteriliyor</small>
                  <Pagination size="sm" className="mb-0">
                    <Pagination.Prev
                      onClick={() => setCurrentAuthorPage(p => Math.max(1, p - 1))}
                      disabled={currentAuthorPage === 1}
                    >
                      Geri
                    </Pagination.Prev>
                    {Array.from({ length: Math.min(5, totalAuthorPages) }, (_, i) => {
                      let pageNum;
                      if (totalAuthorPages <= 5) pageNum = i + 1;
                      else if (currentAuthorPage <= 3) pageNum = i + 1;
                      else if (currentAuthorPage >= totalAuthorPages - 2) pageNum = totalAuthorPages - 4 + i;
                      else pageNum = currentAuthorPage - 2 + i;

                      return (
                        <Pagination.Item
                          key={pageNum}
                          active={currentAuthorPage === pageNum}
                          onClick={() => setCurrentAuthorPage(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>
                      );
                    })}
                    <Pagination.Next
                      onClick={() => setCurrentAuthorPage(p => Math.min(totalAuthorPages, p + 1))}
                      disabled={currentAuthorPage === totalAuthorPages}
                    >
                      İleri
                    </Pagination.Next>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Tümünü Sil Modal */}
      <Modal show={showBulkDeleteModal} onHide={() => setShowBulkDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger me-2" />
            Tüm Gönderileri Sil
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            <strong>{filteredPosts.length}</strong> adet gönderiyi kalıcı olarak silmek istediğinizden emin misiniz?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBulkDeleteModal(false)}>İptal</Button>
          <Button variant="danger" onClick={handleBulkDeleteAll}>
            Evet, {filteredPosts.length} Gönderiyi Sil
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Kullanıcının Tüm Gönderilerini Sil Modal */}
      <Modal show={showUserDeleteModal} onHide={() => setShowUserDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger me-2" />
            Kullanıcı Gönderilerini Sil
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>{deleteTargetUser?.userName}</strong> kullanıcısının <strong>{deleteTargetUser?.count}</strong> adet gönderisini kalıcı olarak silmek istediğinizden emin misiniz?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowUserDeleteModal(false); setDeleteTargetUser(null); }}>İptal</Button>
          <Button variant="danger" onClick={handleDeleteUserPosts}>
            Evet, {deleteTargetUser?.count} Gönderiyi Sil
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Hesap Seçim Modal */}
      <Modal show={showAccountModal} onHide={() => setShowAccountModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Hesap Seçin</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <InputGroup className="mb-3">
            <Form.Control
              type="text"
              placeholder="İsim ile ara..."
              value={accountSearchTerm}
              onChange={(e) => setAccountSearchTerm(e.target.value)}
            />
          </InputGroup>
          <div className="d-flex flex-wrap gap-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {filteredFakeAccounts.map((account) => (
              <div
                key={account.id}
                className="d-flex flex-column align-items-center p-2 border rounded"
                style={{ cursor: 'pointer', width: '120px' }}
                onClick={() => {
                  setSelectedAccount(account);
                  setShowAccountModal(false);
                  setShowPostModal(true);
                  setAccountSearchTerm("");
                }}
              >
                {account.profileImageUrl ? (
                  <img src={account.profileImageUrl} alt={account.name} className="rounded-circle mb-2" style={{ width: 49, height: 49, objectFit: 'cover' }} />
                ) : (
                  <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center mb-2" style={{ width: 49, height: 49 }}>
                    <span style={{ fontSize: '0.9rem', color: '#fff' }}>{account.name.charAt(0).toUpperCase()}</span>
                  </div>
                )}
                <span className="text-center small">{account.name}</span>
              </div>
            ))}
          </div>
        </Modal.Body>
      </Modal>

      {/* Paylaşım Yapma Modal */}
      <Modal show={showPostModal} onHide={() => {
        setShowPostModal(false);
        setSelectedAccount(null);
        setPostText("");
        setSelectedMedia([]);
        setPostType("post");
        setAutoDeleteUnit("day");
        setAutoDeleteValue("");
        setIsPublic(false);
      }} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Paylaşım Yap</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAccount && (
            <div className="mb-3">
              <strong>Seçilen Hesap:</strong> {selectedAccount.name}
            </div>
          )}

          <div className="mb-3">
            <Form.Label className="mb-2"><strong>Paylaşım Tipi</strong></Form.Label>
            <div className="d-flex gap-2">
              <Button variant={postType === "post" ? "primary" : "outline-secondary"} onClick={() => { setPostType("post"); if (postType === "story") { setSelectedMedia([]); } }}>Gönderi</Button>
              <Button variant={postType === "story" ? "primary" : "outline-secondary"} onClick={() => { setPostType("story"); setPostText(""); }}>Hikaye</Button>
            </div>
          </div>

          <div className="mb-3">
            <Form.Label>{postType === "story" ? "Medya (Toplu seçebilirsiniz - Zorunlu)" : "Medya (Toplu seçebilirsiniz - Opsiyonel)"}</Form.Label>
            <Form.Control type="file" accept="image/*,video/*" multiple onChange={handleImageSelect} />
          </div>

          {selectedMedia.length > 0 && (
            <div className="mb-4" style={{ maxHeight: '420px', overflowY: 'auto', paddingRight: '8px' }}>
              {selectedMedia.map((media, index) => (
                <div key={index} className="d-flex align-items-start mb-3 p-3 border rounded shadow-sm bg-white">
                  <div className="position-relative me-3" style={{ width: '120px', height: '120px', flexShrink: 0 }}>
                    {media.file.type.startsWith('video/') ? (
                      <video 
                        src={media.preview} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} 
                        muted 
                        autoPlay 
                        loop
                      />
                    ) : (
                      <img 
                        src={media.preview} 
                        alt={`Preview ${index + 1}`} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} 
                      />
                    )}
                    <Button 
                      variant="danger" 
                      onClick={() => removeMedia(index)}
                      style={{ 
                        position: 'absolute', 
                        top: '-8px', 
                        right: '-8px', 
                        width: '24px', 
                        height: '24px', 
                        padding: 0, 
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px'
                      }}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </div>
                  {postType === "post" ? (
                    <div className="flex-grow-1">
                      <Form.Label className="text-secondary fw-semibold mb-1" style={{ fontSize: '0.85rem' }}>
                        {index + 1}. Medya Açıklaması
                      </Form.Label>
                      <Form.Control 
                        as="textarea" 
                        rows={3} 
                        placeholder={`${index + 1}. Görsel için özel açıklama girin (Opsiyonel)...`}
                        value={media.text} 
                        onChange={(e) => updateMediaText(index, e.target.value)} 
                        style={{ fontSize: '0.95rem', resize: 'none' }}
                      />
                    </div>
                  ) : (
                    <div className="flex-grow-1 d-flex align-items-center h-100" style={{ minHeight: '120px' }}>
                      <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                        {index + 1}. Medya (Hikaye olarak paylaşılacak)
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {postType === "post" && selectedMedia.length === 0 && (
            <div className="mb-3">
              <Form.Label>Metin (Medya eklemediyseniz yazın)</Form.Label>
              <Form.Control as="textarea" rows={4} placeholder="Metin yazın..." value={postText} onChange={(e) => setPostText(e.target.value)} />
            </div>
          )}

          <div className="mb-3">
            <Form.Label><strong>Süre Sonunda Paylaşımı Sil (Opsiyonel)</strong></Form.Label>
            <div className="d-flex gap-2 align-items-end">
              <div className="flex-grow-1">
                <Form.Control type="number" placeholder="Süre girin..." value={autoDeleteValue} onChange={(e) => setAutoDeleteValue(e.target.value)} min="0" step="0.1" />
              </div>
              <div>
                <Form.Select value={autoDeleteUnit} onChange={(e) => setAutoDeleteUnit(e.target.value)}>
                  <option value="hour">Saat</option>
                  <option value="day">Gün</option>
                </Form.Select>
              </div>
            </div>
            <Form.Text className="text-muted">
              {autoDeleteValue && !isNaN(autoDeleteValue) && parseFloat(autoDeleteValue) > 0 ? (
                autoDeleteUnit === "hour"
                  ? `${autoDeleteValue} saat sonunda otomatik silinecek`
                  : `${autoDeleteValue} gün sonunda otomatik silinecek`
              ) : ("Boş bırakılırsa paylaşım silinmez")}
            </Form.Text>
          </div>

          <div className="mb-3">
            <Form.Check type="switch" id="is-public" label="Herkese Açık Yap (Ör: Eğitmen vb Gold profiller için paywall'a takılmasın, herkes görebilsin)" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowPostModal(false); setSelectedAccount(null); setPostText(""); setSelectedMedia([]); setPostType("post"); setAutoDeleteUnit("day"); setAutoDeleteValue(""); }}>İptal</Button>
          <Button variant="primary" onClick={handlePost} disabled={posting}>{posting ? "Gönderiliyor..." : (selectedMedia.length > 1 ? `Paylaş (${selectedMedia.length})` : "Paylaş")}</Button>
        </Modal.Footer>
      </Modal>

      {/* Yorumlar Modal */}
      <Modal show={showCommentsModal} onHide={() => { setShowCommentsModal(false); setSelectedPostForComments(null); setComments([]); }} size="xl">
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center w-100 pe-3">
            <div className="me-auto">
              Yorumlar
              {selectedPostForComments && (
                <small className="text-muted ms-2">
                  ({selectedPostForComments.authorName} - {selectedPostForComments.text?.substring(0, 30)}...)
                </small>
              )}
            </div>
            {comments.length > 0 && (
              <Button 
                variant="danger" 
                size="sm" 
                onClick={handleDeleteAllComments} 
                disabled={actionLoading === "deleteAllComments"}
              >
                {actionLoading === "deleteAllComments" ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <>
                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                    Tüm Yorumları Sil
                  </>
                )}
              </Button>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingComments ? (
            <div className="text-center py-4">
              <div className="spinner-border" role="status"><span className="visually-hidden">Yükleniyor...</span></div>
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-4"><p className="text-muted">Henüz yorum yok.</p></div>
          ) : (
            <div className="table-responsive">
              <Table className="table-centered mb-0">
                <thead className="thead-light">
                  <tr>
                    <th>#</th>
                    <th>Yazar</th>
                    <th>Yorum</th>
                    <th>Tarih</th>
                    <th>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {comments.map((comment, index) => (
                    <tr key={comment.id}>
                      <td>{index + 1}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          {comment.authorAvatar ? (
                            <img src={comment.authorAvatar} alt={comment.authorName} className="rounded-circle me-2" style={{ width: 32, height: 32, objectFit: 'cover' }} />
                          ) : (
                            <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center me-2" style={{ width: 32, height: 32 }}>
                              <span style={{ fontSize: '0.7rem', color: '#fff' }}>{comment.authorName?.charAt(0)?.toUpperCase() || '?'}</span>
                            </div>
                          )}
                          <span>{comment.authorName || 'İsimsiz'}</span>
                        </div>
                      </td>
                      <td>
                        {editingCommentId === comment.id ? (
                          <div className="d-flex gap-2">
                            <Form.Control
                              size="sm"
                              type="text"
                              value={editingCommentText}
                              onChange={(e) => setEditingCommentText(e.target.value)}
                            />
                            <Button variant="success" size="sm" onClick={() => handleSaveComment(comment.id)}>
                              Kaydet
                            </Button>
                            <Button variant="secondary" size="sm" onClick={() => setEditingCommentId(null)}>
                              İptal
                            </Button>
                          </div>
                        ) : (
                          <div style={{ maxWidth: '300px', wordWrap: 'break-word' }}>{comment.text || '-'}</div>
                        )}
                      </td>
                      <td>{formatDate(comment.createdAt)}</td>
                      <td>
                        <div className="btn-group">
                          <Button variant="outline-primary" size="sm" onClick={() => handleEditComment(comment)} title="Düzenle">
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                          <Button variant="outline-warning" size="sm" onClick={() => handleCensorComment(comment.id)} title="Sansürle">
                            <FontAwesomeIcon icon={faBan} />
                          </Button>
                          <Button variant="outline-danger" size="sm" onClick={() => handleDeleteComment(comment.id)} disabled={deletingCommentId === comment.id} title="Yorumu Sil">
                            {deletingCommentId === comment.id ? (<span className="spinner-border spinner-border-sm" />) : (<FontAwesomeIcon icon={faTrash} />)}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowCommentsModal(false); setSelectedPostForComments(null); setComments([]); }}>Kapat</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
